import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

type Check = {
  url: string;
  status: number | null;
  canonical: { found: string | null; matches: boolean };
  robots: { found: string | null; indexable: boolean };
  inSitemap: boolean;
  title: string | null;
  description: string | null;
  ogTitle: string | null;
  ogImage: string | null;
  errors: string[];
};

type Report = {
  summary: {
    origin: string;
    checkedAt: string;
    totalRoutes: number;
    passing: number;
    failing: number;
    sitemapUrls: number;
    robotsTxt: { ok: boolean; status: number | null; allowsAll: boolean; sitemapRef: boolean };
  };
  checks: Check[];
};

export const Route = createFileRoute("/seo-check")({
  head: () => ({
    meta: [
      { title: "SEO Check — Hantavirus International" },
      { name: "description", content: "Internal SEO audit tool." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SeoCheckPage,
});

function Pill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        ok ? "bg-green-500/15 text-green-500" : "bg-red-500/15 text-red-500"
      }`}
    >
      {ok ? "✓" : "✗"} {label}
    </span>
  );
}

function SeoCheckPage() {
  const [origin, setOrigin] = useState("https://hantavirus.international");
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setOrigin(window.location.origin);
    }
  }, []);

  async function run() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/seo-check?origin=${encodeURIComponent(origin)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Report;
      setReport(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background px-6 py-10 text-foreground">
      <div className="mx-auto max-w-5xl">
        <h1 className="text-2xl font-bold">SEO Check</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Validates canonical, robots meta, sitemap inclusion, title, description and og tags
          for every public route.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <input
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            className="flex-1 min-w-[260px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="https://hantavirus.international"
          />
          <button
            onClick={run}
            disabled={loading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? "Running…" : "Run check"}
          </button>
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>
        )}

        {report && (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Routes" value={report.summary.totalRoutes} />
              <Stat label="Passing" value={report.summary.passing} good />
              <Stat label="Failing" value={report.summary.failing} bad={report.summary.failing > 0} />
              <Stat label="In sitemap" value={report.summary.sitemapUrls} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <Pill ok={report.summary.robotsTxt.ok} label="robots.txt reachable" />
              <Pill ok={report.summary.robotsTxt.allowsAll} label="robots.txt allows crawling" />
              <Pill ok={report.summary.robotsTxt.sitemapRef} label="robots.txt references sitemap" />
            </div>

            <p className="mt-2 text-xs text-muted-foreground">
              Checked at {new Date(report.summary.checkedAt).toLocaleString()} · origin {report.summary.origin}
            </p>

            <div className="mt-6 space-y-3">
              {report.checks.map((c) => {
                const ok = c.errors.length === 0 && c.inSitemap;
                return (
                  <div
                    key={c.url}
                    className={`rounded-lg border p-4 ${
                      ok ? "border-border" : "border-red-500/40 bg-red-500/5"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-sm hover:underline"
                      >
                        {c.url}
                      </a>
                      <span className="text-xs text-muted-foreground">HTTP {c.status ?? "—"}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Pill ok={c.canonical.matches} label="canonical" />
                      <Pill ok={c.robots.indexable} label="robots indexable" />
                      <Pill ok={c.inSitemap} label="in sitemap" />
                      <Pill ok={!!c.title} label="title" />
                      <Pill ok={!!c.description} label="description" />
                      <Pill ok={!!c.ogTitle} label="og:title" />
                      <Pill ok={!!c.ogImage} label="og:image" />
                    </div>
                    <dl className="mt-3 grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-[120px_1fr]">
                      <dt>Title</dt>
                      <dd className="text-foreground">{c.title ?? "—"}</dd>
                      <dt>Description</dt>
                      <dd className="text-foreground">{c.description ?? "—"}</dd>
                      <dt>Canonical</dt>
                      <dd className="break-all text-foreground">{c.canonical.found ?? "—"}</dd>
                      <dt>Robots</dt>
                      <dd className="text-foreground">{c.robots.found ?? "—"}</dd>
                      <dt>OG image</dt>
                      <dd className="break-all text-foreground">{c.ogImage ?? "—"}</dd>
                    </dl>
                    {c.errors.length > 0 && (
                      <ul className="mt-3 list-inside list-disc text-xs text-red-500">
                        {c.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, good, bad }: { label: string; value: number; good?: boolean; bad?: boolean }) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        good ? "border-green-500/40" : bad ? "border-red-500/40" : "border-border"
      }`}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}
